import dayjs, { type Dayjs } from 'dayjs';

import { type ClassSessionWithInstructors, type Direction, DirectionOption } from '../types';

interface FilterBySearchProps {
    classSessions: ClassSessionWithInstructors[];
    searchValue: string;
}

export const filterBySearch = ({ classSessions, searchValue }: FilterBySearchProps): ClassSessionWithInstructors[] => classSessions.filter((classSessionValue) => {
    if (searchValue.length >= 1) {
        const classSessionName = classSessionValue?.class_session_topic;
        const matchesClassSessionName = classSessionName ? classSessionName.toLocaleLowerCase().includes(searchValue.trim().toLocaleLowerCase()) : false;

        const classSessionUuid = classSessionValue?.class_session_uuid ?? '';
        const meetingId = classSessionValue?.meeting_id ?? '';

        const matchesClassSessionUuid = (searchValue.length > 5) && classSessionUuid.toLocaleLowerCase().includes(searchValue.trim().toLocaleLowerCase());
        const matchesMeetingUuid = (searchValue.length > 5) && meetingId.toLocaleLowerCase().includes(searchValue.trim().toLocaleLowerCase());

        return matchesClassSessionName || matchesClassSessionUuid || matchesMeetingUuid;
    }
    return true;
});

interface ReturnClassSessionDateStringProps {
    index: 'first' | 'last';
    classSessionDates?: string | null;
}

export const returnClassSessionDateString = ({ classSessionDates, index }: ReturnClassSessionDateStringProps) => {
    if (typeof classSessionDates !== 'string') return null;

    const classSessionDatesArray = classSessionDates.split(',');
    const indexNumber = index === 'first' ? 0 : classSessionDatesArray.length - 1;
    const date = classSessionDatesArray[indexNumber];

    if (!date) return null;

    const lastClassSessionDate = date.split(':')[0];

    if (!lastClassSessionDate) return null;

    return lastClassSessionDate;
};

interface FilterByStartDateProps {
    classSessions: ClassSessionWithInstructors[];
    startDate: Dayjs | null;
}

export const filterByStartDate = ({ classSessions, startDate }: FilterByStartDateProps): ClassSessionWithInstructors[] => classSessions.filter((classSessionValue) => {
    if (!startDate) return true;

    const classSessionStartDateString = classSessionValue.starting_at;

    if (!classSessionStartDateString) {
        return false;
    }

    const classSessionStartDate = dayjs(classSessionStartDateString);

    const isBeforeSelectedDay = startDate.isBefore(classSessionStartDate.toISOString(), 'day');
    const isSameSelectedDay = startDate.isSame(classSessionStartDate.toISOString(), 'day');

    return isBeforeSelectedDay || isSameSelectedDay;
});

interface FilterByEndDateProps {
    classSessions: ClassSessionWithInstructors[];
    endDate: Dayjs | null;
}

export const filterByEndDate = ({ classSessions, endDate }: FilterByEndDateProps): ClassSessionWithInstructors[] => classSessions.filter((classSessionValue) => {
    if (!endDate) return true;

    const classSessionEndDateString = classSessionValue.ending_at;

    if (!classSessionEndDateString) {
        return false;
    }

    const classSessionEndDate = dayjs(classSessionEndDateString);

    const isAfterSelectedDay = endDate.isAfter(classSessionEndDate.toISOString(), 'day');
    const isSameSelectedDay = endDate.isSame(classSessionEndDate.toISOString(), 'day');

    return isAfterSelectedDay || isSameSelectedDay;
});

interface SortByProps {
    classSessions: ClassSessionWithInstructors[];
    sort: Direction;
}

export const sortByClassSessionName = ({ classSessions, sort }: SortByProps): ClassSessionWithInstructors[] => {
    switch (sort) {
        case DirectionOption.ASCENDING:
            return classSessions.sort((classSessionA, classSessionB) => classSessionA.class_session_topic!.trim().localeCompare(classSessionB.class_session_topic!.trim()));
        case DirectionOption.DESCENDING:
            return classSessions.sort((classSessionA, classSessionB) => classSessionB.class_session_topic!.trim().localeCompare(classSessionA.class_session_topic!.trim()));
        default:
            return classSessions;
    }
};

export const sortByDates = ({ classSessions, sort }: SortByProps): ClassSessionWithInstructors[] => {
    switch (sort) {
        case DirectionOption.ASCENDING:
            return classSessions.sort((a, b) => {
                if (!a.starting_at) return 1;
                if (!b.starting_at) return -1;
                return new Date(a.starting_at).getTime() - new Date(b.starting_at).getTime();
            });
        case DirectionOption.DESCENDING:
            return classSessions.sort((a, b) => {
                if (!a.starting_at) return -1;
                if (!b.starting_at) return 1;
                return new Date(b.starting_at).getTime() - new Date(a.starting_at).getTime();
            });
        default:
            return classSessions;
    }
};

const returnInstructorFirstName = (classSessionWithInstructors: ClassSessionWithInstructors): string | undefined | null => classSessionWithInstructors?.instructors[0]?.user_name;

export const sortByInstructors = ({ classSessions, sort }: SortByProps): ClassSessionWithInstructors[] => {
    switch (sort) {
        case DirectionOption.ASCENDING:
            return classSessions.sort((classSessionA, classSessionB) => {
                const instructorNameA = returnInstructorFirstName(classSessionA);
                const instructorNameB = returnInstructorFirstName(classSessionB);

                if (instructorNameA && instructorNameB) {
                    return instructorNameA.trim().localeCompare(instructorNameB.trim());
                }

                return instructorNameA ? -1 : 1;
            });
        case DirectionOption.DESCENDING:
            return classSessions.sort((classSessionA, classSessionB) => {
                const instructorNameA = returnInstructorFirstName(classSessionA);
                const instructorNameB = returnInstructorFirstName(classSessionB);

                if (instructorNameA && instructorNameB) {
                    return instructorNameB.trim().localeCompare(instructorNameA.trim());
                }

                return instructorNameA ? -1 : 1;
            });
        default:
            return classSessions;
    }
};
